import { Link } from "react-router-dom";
import Seo from "@/components/seo/Seo";
import Reveal from "@/components/ui-primitives/Reveal";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { articles, CANONICAL_ORIGIN, executive } from "@/data/site";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function ThoughtLeadership() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${CANONICAL_ORIGIN}/leadership/thought-leadership#blog`,
    url: `${CANONICAL_ORIGIN}/leadership/thought-leadership`,
    name: "Adeshi Moffi — Thought Leadership",
    author: { "@id": `${CANONICAL_ORIGIN}/leadership#adeshi-moffi` },
    publisher: { "@id": `${CANONICAL_ORIGIN}/#organization` },
    blogPost: articles.map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      description: a.dek,
      url: `${CANONICAL_ORIGIN}/leadership/thought-leadership/${a.slug}`,
      datePublished: a.date,
      author: { "@id": `${CANONICAL_ORIGIN}/leadership#adeshi-moffi` },
      image: a.image,
    })),
  };

  return (
    <>
      <Seo
        title="Thought Leadership — Adeshi Moffi | Investors Pathway"
        description="Perspectives from Adeshi Moffi on private equity, capital raising, consumer brands, manufacturing, retail growth, business strategy, family office investing, and entrepreneurship."
        path="/leadership/thought-leadership"
        image={executive.headshot}
        jsonLd={jsonLd}
      />
      <section className="pt-28 md:pt-36 pb-12">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Leadership", to: "/leadership" }, { label: "Thought Leadership" }]} />
          <Reveal>
            <div className="mt-10 section-num">05 / Thought Leadership</div>
            <h1 data-testid="tl-title" className="h-display text-[3rem] md:text-[4.6rem] text-[#0A1128] mt-6 max-w-5xl">
              Perspectives from the office of the president.
            </h1>
            <p className="mt-6 body-lead max-w-3xl">
              Institutional commentary on capital, brands, distribution, and the
              operating disciplines that compound enterprise value.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          {/* Featured */}
          <Reveal>
            <Link
              to={`/leadership/thought-leadership/${articles[0].slug}`}
              data-testid="featured-article"
              className="group block border-t border-b border-[#0A1128] py-10 grid grid-cols-12 gap-8 hover:bg-[#F5F3EE] transition-colors duration-300"
            >
              <div className="col-span-12 md:col-span-6 overflow-hidden">
                <img
                  src={articles[0].image}
                  alt={articles[0].title}
                  loading="lazy"
                  className="w-full h-[360px] object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
              </div>
              <div className="col-span-12 md:col-span-6 flex flex-col justify-center">
                <div className="eyebrow text-[#8E7331]">Featured · {articles[0].category}</div>
                <h2 className="h-editorial text-3xl md:text-5xl text-[#0A1128] mt-6 leading-tight">
                  {articles[0].title}
                </h2>
                <p className="mt-6 body-lead">{articles[0].dek}</p>
                <div className="mt-8 flex justify-between text-xs uppercase tracking-[0.16em] text-[#475569] font-sans">
                  <span>{fmt(articles[0].date)}</span>
                  <span>{articles[0].readTime}</span>
                </div>
              </div>
            </Link>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="tl-grid">
            {articles.slice(1).map((a) => (
              <Reveal key={a.slug}>
                <Link
                  to={`/leadership/thought-leadership/${a.slug}`}
                  data-testid={`tl-card-${a.slug}`}
                  className="group block border border-[#E2E8F0] bg-white hover:border-[#B89947] transition-colors duration-300"
                >
                  <div className="overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-56 object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="eyebrow text-[#8E7331]">{a.category}</div>
                    <h3 className="h-editorial text-xl md:text-2xl text-[#0A1128] mt-4 leading-snug">
                      {a.title}
                    </h3>
                    <p className="mt-4 text-sm text-[#475569] font-sans leading-relaxed line-clamp-3">
                      {a.dek}
                    </p>
                    <div className="mt-6 flex justify-between text-xs uppercase tracking-[0.16em] text-[#475569] font-sans">
                      <span>{fmt(a.date)}</span>
                      <span>{a.readTime}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
